import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Share2, Copy, MessageCircle, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = 'coachhub_review_link';
const CACHE_MS = 10 * 60 * 1000;

function readCache(userId) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    if (cached && cached.userId === userId && Date.now() - cached.at < CACHE_MS) return cached.data;
  } catch (e) { /* storage unavailable */ }
  return null;
}

/** The institute's review link for the logged-in user ({ available, url, institute_name }), cached for the session. */
export function useReviewLink(user) {
  const [data, setData] = useState(() => readCache(user?.id));
  useEffect(() => {
    if (!user?.id || readCache(user.id)) return undefined;
    let cancelled = false;
    axios.get(`${API}/google-reviews/my-link`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ userId: user.id, at: Date.now(), data: res.data })); } catch (e) { /* ignore */ }
      })
      .catch(() => { if (!cancelled) setData({ available: false }); });
    return () => { cancelled = true; };
  }, [user?.id]);
  return data;
}

function shareText(link) {
  const name = link?.institute_name ? ` ${link.institute_name}` : '';
  return `Please share your experience with${name} — it takes a minute: ${link?.url}`;
}

export function ReviewActions({ link, compact = false }) {
  const [copied, setCopied] = useState(false);
  if (!link?.available) return null;
  const text = shareText(link);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true); toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { toast.error('Could not copy the link'); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'Review us', text, url: link.url }); } catch (e) { /* cancelled */ }
    } else {
      copy();
    }
  };

  const btn = 'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold transition active:scale-[0.98]';
  return (
    <div className={compact ? 'grid grid-cols-2 gap-2' : 'grid gap-2.5 sm:grid-cols-2'}>
      <a href={link.url} target="_blank" rel="noopener noreferrer" data-testid="review-write"
        className={`${btn} col-span-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm hover:from-amber-600 hover:to-orange-600`}>
        <Star className="h-4 w-4 fill-white" /> Write a review <ExternalLink className="h-3.5 w-3.5 opacity-80" />
      </a>
      <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" data-testid="review-whatsapp"
        className={`${btn} bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300`}>
        <MessageCircle className="h-4 w-4" /> WhatsApp
      </a>
      <button type="button" onClick={navigator.share ? share : copy} data-testid="review-share"
        className={`${btn} bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200`}>
        {navigator.share ? <><Share2 className="h-4 w-4" /> Share</> : <><Copy className="h-4 w-4" /> {copied ? 'Copied' : 'Copy link'}</>}
      </button>
    </div>
  );
}

/** Small card for the student / parent home screen; renders nothing when reviews are not set up. */
export default function ReviewInviteCard({ user }) {
  const link = useReviewLink(user);
  if (!link?.available) return null;
  return (
    <div className="rounded-3xl border border-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50 p-4 sm:p-5 dark:border-amber-500/20 dark:from-amber-500/10 dark:to-orange-500/5" data-testid="review-invite-card">
      <div className="mb-3 flex items-start gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-amber-500 text-white"><Star className="h-5 w-5 fill-white" /></span>
        <div className="min-w-0">
          <h3 className="font-outfit text-base font-bold text-slate-900 dark:text-white">Enjoying {link.institute_name || 'your institute'}?</h3>
          <p className="text-xs text-slate-600 dark:text-slate-300">A short review helps other students and parents. You can share the link with friends and family too.</p>
        </div>
      </div>
      <ReviewActions link={link} compact />
    </div>
  );
}
